import * as React from "react";
import { Download, RotateCcw } from "lucide-react";

import { ConnectProto } from "@racoon/api";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselThumbnails,
  type CarouselApi,
} from "@racoon/ui";
import { Namespace, useT, useDateFnsLocale } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";

import { LocationPhotosSlider } from "~/features/pickup-location-modal/components/location-photos-slider";
import { formatReportTime } from "../model/report-time";

/** A photo, labelled «До», «После» or with why the job failed. */
export type ReportPhoto = {
  photo: ConnectProto.FileUpload;
  label: string;
  /** «Забрали» / «Поставили», before the container name. */
  containerAction: string;
};

interface PhotoReportCarouselProps {
  items: ReportPhoto[];
  /** Rendered over the bottom-right corner of the main photo. */
  overlayAction?: React.ReactNode;
}

export const PhotoReportCarousel: React.FC<PhotoReportCarouselProps> = ({
  items,
  overlayAction,
}) => {
  const t = useT();
  const dateLocale = useDateFnsLocale();
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [isSliderOpen, setIsSliderOpen] = React.useState(false);
  const [sliderStartIndex, setSliderStartIndex] = React.useState(0);
  // View-only rotation in degrees, keyed by photo URL (stable across uploads,
  // unique even for legacy photos with id 0); not persisted.
  const [rotations, setRotations] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    if (!carouselApi) {
      return;
    }

    setCurrent(carouselApi.selectedScrollSnap());

    carouselApi.on("select", () => {
      setCurrent(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  const photos = items.map(({ photo }) => photo);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative w-full rounded-lg overflow-hidden">
        <Carousel setApi={setCarouselApi} className="w-full h-full">
          <CarouselContent className="h-full">
            {items.map(({ photo, label, containerAction }, index) => {
              const photoAt = fromTimestamp(photo.createdAt);
              const rotation = rotations[photo.url] ?? 0;
              // A quarter-turned 4:3 box is 3:4 — shrink it so it still fits the frame.
              const scale = rotation % 180 === 0 ? 1 : 3 / 4;

              return (
                // Legacy photos share id 0
                <CarouselItem key={`${photo.id}-${photo.url}`} className="relative h-full pl-0">
                  <div
                    onDoubleClick={() => {
                      setSliderStartIndex(index);
                      setIsSliderOpen(true);
                    }}
                    // 440: what the report body leaves between the tabs and the thumbnails
                    className="relative w-full h-[440px] flex items-center justify-center bg-black rounded-lg overflow-hidden"
                  >
                    <div
                      className="max-w-full max-h-full aspect-[4/3] w-full rounded-lg bg-cover bg-center bg-no-repeat transition-transform"
                      style={{
                        backgroundImage: `url("${photo.url}")`,
                        transform: `rotate(${rotation}deg) scale(${scale})`,
                      }}
                    />

                    <div className="absolute flex justify-between items-start gap-2 top-2 left-2 right-2 z-10">
                      <div className="flex items-center text-sm text-white font-medium bg-surface-invert rounded-lg h-[32px] overflow-hidden">
                        <div className="p-2 uppercase">{label}</div>
                        {photoAt && (
                          <div className="p-2 border-l border-text-primary">
                            {formatReportTime(photoAt, dateLocale)}
                          </div>
                        )}
                      </div>

                      {photo.containerName && (
                        <div className="p-2 gap-2 flex items-center text-sm text-white font-medium bg-surface-invert rounded-lg h-[32px] overflow-hidden">
                          <div className="uppercase">{containerAction}</div>
                          <div>
                            <span className="text-brand-blue">#</span> {photo.containerName}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
        <button
          type="button"
          onClick={() => {
            const url = photos[current]?.url;
            if (!url) return;
            setRotations((prev) => ({ ...prev, [url]: (prev[url] ?? 0) - 90 }));
          }}
          aria-label={t(Namespace.PhotoReport, "rotatePhoto")}
          title={t(Namespace.PhotoReport, "rotatePhoto")}
          className="absolute bottom-2 left-2 z-10 flex items-center justify-center size-8 rounded-lg bg-white text-brand-blue hover:opacity-80"
        >
          <RotateCcw className="size-4" />
        </button>
        {photos.length > 1 && (
          <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 gap-4">
            {photos.map((photo, index) => (
              <button
                key={`${photo.id}-${photo.url}`}
                type="button"
                aria-label={String(index + 1)}
                onClick={() => carouselApi?.scrollTo(index)}
                className={`size-2 rounded-full ${index === current ? "bg-brand-blue" : "bg-white"}`}
              />
            ))}
          </div>
        )}
        <div className="absolute bottom-2 right-2 z-10 flex gap-2">
          {overlayAction}
          <a
            href={photos[current]?.url}
            download
            target="_blank"
            rel="noreferrer"
            aria-label={t(Namespace.PhotoReport, "downloadPhoto")}
            title={t(Namespace.PhotoReport, "downloadPhoto")}
            className="flex items-center justify-center size-8 rounded-lg bg-white text-brand-blue hover:opacity-80"
          >
            <Download className="size-4" />
          </a>
        </div>
      </div>

      {/* Thumbnails */}
      {photos.length > 1 && (
        <CarouselThumbnails mainApi={carouselApi} className="w-full">
          {photos.map((photo, idx) => (
            <div
              key={`${photo.id}-${photo.url}`}
              className={`relative aspect-[3/2] h-20 rounded-lg overflow-hidden transition-all ${
                idx === current ? "border-[3px] border-brand-blue" : ""
              }`}
            >
              <div
                className="w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url("${photo.url}")` }}
              />
            </div>
          ))}
        </CarouselThumbnails>
      )}

      <LocationPhotosSlider
        photos={photos}
        open={isSliderOpen}
        onOpenChange={setIsSliderOpen}
        initialSlide={sliderStartIndex}
        title={items[sliderStartIndex]?.label ?? ""}
      />
    </div>
  );
};
